import {
  Box,
  Button,
  Modal as ChakraModal,
  Checkbox,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Product } from '../../../Interfaces/products'

interface PrintEtiquetaModalProps {
  isOpen: boolean
  onClose: () => void
  products: Product[]
  onConfirm: (selectedProductIds: number[], multipleProductsOnSameLabel: boolean) => void
  isLoading?: boolean
}

export function PrintEtiquetaModal({
  isOpen,
  onClose,
  products,
  onConfirm,
  isLoading = false,
}: PrintEtiquetaModalProps) {
  const [selectedProducts, setSelectedProducts] = useState<number[]>([])
  const [multipleProductsOnSameLabel, setMultipleProductsOnSameLabel] = useState(false)

  useEffect(() => {
    // Quando o modal abrir, selecionar todos os produtos por padrão
    if (isOpen && products.length > 0) {
      setSelectedProducts(products.map((p) => p.id!).filter((id) => id !== undefined))
      setMultipleProductsOnSameLabel(false)
    }
  }, [isOpen, products])

  const handleToggleProduct = (productId: number) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId)
      } else {
        return [...prev, productId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([])
    } else {
      setSelectedProducts(products.map((p) => p.id!).filter((id) => id !== undefined))
    }
  }

  const handleConfirm = () => {
    if (selectedProducts.length === 0) {
      return
    }
    onConfirm(selectedProducts, multipleProductsOnSameLabel)
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bgColor="gray.800">
        <ModalHeader>Selecionar Produtos para Impressão</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSelectAll}
                mb={4}
              >
                {selectedProducts.length === products.length
                  ? 'Desmarcar Todos'
                  : 'Selecionar Todos'}
              </Button>
              <Text fontSize="sm" color="gray.400" mb={2}>
                {selectedProducts.length} de {products.length} produto(s) selecionado(s)
              </Text>
            </Box>
            <Divider />
            <Box>
              <Checkbox
                isChecked={multipleProductsOnSameLabel}
                onChange={(e) => setMultipleProductsOnSameLabel(e.target.checked)}
                colorScheme="blue"
              >
                <Text fontSize="sm" color="gray.300">
                  Imprimir múltiplos produtos na mesma etiqueta
                </Text>
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                Se marcado, apenas a primeira etiqueta terá cabeçalho completo (logo, OS, datas, cliente).
                Os demais produtos serão impressos em sequência, economizando espaço.
              </Text>
            </Box>
            <Divider />
            <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
              {products.length === 0 ? (
                <Text color="gray.400" textAlign="center" py={4}>
                  Nenhum produto encontrado
                </Text>
              ) : (
                products.map((product) => (
                  <Box
                    key={product.id}
                    p={3}
                    bg="gray.700"
                    borderRadius="md"
                    border="1px"
                    borderColor={
                      selectedProducts.includes(product.id!)
                        ? 'blue.500'
                        : 'gray.600'
                    }
                  >
                    <Checkbox
                      isChecked={selectedProducts.includes(product.id!)}
                      onChange={() => handleToggleProduct(product.id!)}
                      colorScheme="blue"
                    >
                      <Box ml={3}>
                        <Text fontWeight="bold">{product.description}</Text>
                        <Text fontSize="sm" color="gray.400">
                          Quantidade: {product.amount} | Tonalidade:{' '}
                          {product.ink_color?.name || 'N/A'}
                        </Text>
                      </Box>
                    </Checkbox>
                  </Box>
                ))
              )}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isDisabled={selectedProducts.length === 0 || isLoading}
            isLoading={isLoading}
          >
            Imprimir ({selectedProducts.length})
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}

